<template>
  <!-- Карточка оценки в ленте решённого обращения (p5, p5a, p5b) -->
  <div class="rating-card">
    <!-- Смена заголовка за 250 мс: оба в одной ячейке сетки, высота не прыгает (П22) -->
    <div class="rating-card__title">
      <transition name="rating-swap">
        <span v-if="rated" key="thanks">Спасибо за оценку!</span>
        <span v-else key="ask">Оцените скорость и качество поддержки</span>
      </transition>
    </div>
    <div class="rating-card__stars" @mouseleave="hover = 0">
      <button
        v-for="n in 5"
        :key="n"
        type="button"
        class="rating-card__star"
        :class="{ 'rating-card__star--jump': jump }"
        :style="{ animationDelay: `${(n - 1) * 70}ms` }"
        :disabled="rated || sending"
        :aria-label="`Оценка ${n} из 5`"
        @mouseenter="hover = n"
        @click="choose(n)"
      >
        <v-icon size="30">
          {{ starIcon(n) }}
        </v-icon>
      </button>
    </div>
    <!-- Строка всегда в разметке: лента прижата к низу, и появление подписи поднимало звёзды
         из-под курсора - наведение мигало, клик по звезде терялся -->
    <div class="rating-card__caption">
      {{ shown ? caption : '' }}
    </div>

    <!-- 1-3 звезды: необязательное уточнение, оно уходит в комментарий оценки (Q21) -->
    <div v-if="askComment" class="rating-card__comment">
      <textarea
        v-model="comment"
        class="rating-card__textarea"
        rows="2"
        maxlength="1000"
        placeholder="Что пошло не так?"
      />
      <v-btn
        depressed
        height="40"
        class="rating-card__submit text-normalize"
        :loading="sending"
        @click="submit"
      >
        Отправить
      </v-btn>
    </div>
  </div>
</template>

<script>
  import { launchConfetti } from '../confetti'
  import { ratingLine } from '../format'

  // Ниже этой оценки спрашиваем, что пошло не так.
  const ASK_BELOW = 4

  export default {
    name: 'RatingCard',
    props: {
      rating: { type: Object, default: null },
      sending: { type: Boolean, default: false },
    },
    data () {
      return { hover: 0, selected: 0, comment: '', jump: false }
    },
    computed: {
      rated () {
        return Boolean(this.rating)
      },
      // Поставленная оценка, иначе наведение, иначе выбранная низкая, ждущая комментария.
      shown () {
        return this.rating?.score || this.hover || this.selected
      },
      caption () {
        return ratingLine(this.shown)
      },
      askComment () {
        return !this.rated && this.selected > 0 && this.selected < ASK_BELOW
      },
    },
    watch: {
      // Только что поставили 5: звёзды подпрыгивают по очереди и бьют конфетти (П22, П23).
      // На 1-4 - только смена заголовка, «без праздника».
      rating (value, previous) {
        if (previous || !value || value.score !== 5) return
        this.jump = true
        launchConfetti(this.$el, this.$el.closest('.feed-list'))
      },
    },
    methods: {
      // Звёзды до наведения или оценки залиты, остальные контурные (p5a).
      starIcon (n) {
        return n > this.shown ? 'mdi-star-outline' : 'mdi-star'
      },
      choose (score) {
        if (this.rated) return
        if (score < ASK_BELOW) {
          this.selected = score
          return
        }
        this.$emit('rate', { score, comment: null })
      },
      submit () {
        this.$emit('rate', { score: this.selected, comment: this.comment.trim() || null })
      },
    },
  }
</script>

<style scoped lang="less">
@import '../tokens.less';

.rating-card {
  align-self: center;
  width: 520px;
  max-width: 100%;
  padding: 20px 24px;
  border-radius: 8px;
  text-align: center;
  background: @fakt-cream-soft;
}

.rating-card__title {
  display: grid;
  font-size: 15px;
  font-weight: 500;
  color: @fakt-text;
}

.rating-card__title > * {
  grid-area: 1 / 1;
}

.rating-swap-enter-active,
.rating-swap-leave-active {
  transition: opacity 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.rating-swap-enter,
.rating-swap-leave-to {
  opacity: 0;
}

/* Прыжок по очереди: 350 мс каждая, задержки 0-280 мс из :style */
.rating-card__star--jump {
  animation: rating-star-jump 0.35s both;
}

@keyframes rating-star-jump {
  0% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
  }
  50% {
    transform: scale(1.3);
    animation-timing-function: ease-in;
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .rating-card__star--jump {
    animation: none;
  }

  .rating-swap-enter-active,
  .rating-swap-leave-active {
    transition: none;
  }
}

.rating-card__stars {
  display: inline-flex;
  gap: 8px;
  margin-top: 12px;
}

.rating-card__star {
  .v-icon {
    color: @fakt-orange !important;
    transition: transform 0.12s ease;
  }

  &:not(:disabled):hover .v-icon {
    transform: scale(1.1);
  }

  @media (prefers-reduced-motion: reduce) {
    .v-icon {
      transition: none;
    }
  }
}

.rating-card__caption {
  min-height: 18px;
  margin-top: 6px;
  font-size: 13px;
  line-height: 18px;
  color: @fakt-text-2;
}

.rating-card__comment {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-top: 12px;
  text-align: left;
}

.rating-card__textarea {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid @fakt-cream-border;
  border-radius: 10px;
  font-size: 14px;
  resize: none;
  outline: none;
  background: @fakt-white;

  &:focus {
    border-color: @fakt-orange;
  }
}

.rating-card__submit {
  border-radius: 10px;
  color: @fakt-white !important;
  background-color: @fakt-orange !important;
}
</style>
